import { ChangeDetectorRef, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FeatureVisibility } from '@selfCareShared/models/common.models';
import { SharedDataService } from '@selfCareShared/services/shared-data.service';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMenuDirective, NzMenuGroupComponent, NzMenuItemComponent } from 'ng-zorro-antd/menu';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { filter } from 'rxjs';

export type SidebarMenuItem = {
  title: string;
  link: string;
  iconSrc?: string;
  nzTheme?: 'fill' | 'outline' | 'twotone';
  isActive?: boolean;
  /** Hidden when this flag is explicitly `false` (same as templates: `visibility?.key ?? true`). */
  visibilityKey?: keyof FeatureVisibility;
  /** Shown if at least one flag is not explicitly `false`. */
  visibilityAnyOf?: readonly (keyof FeatureVisibility)[];
};

export type Menu = {
  header?: string;
  id?: string | number;
  items?: SidebarMenuItem[];
};

/** Match support hub + `featureVisibilityGuard` on `/support`. */
const SUPPORT_MENU_ANY_FEATURES: readonly (keyof FeatureVisibility)[] = [
  'liveChatVisible',
  'callSupportVisible',
  'submitComplaintsVisible',
  'myRequestVisible',
  'faqAndGuideVisible',
  'whatsAppVisible',
  'messengerVisible',
  'feedbackVisible',
];

function isSidebarItemVisible(item: SidebarMenuItem, fv: Partial<FeatureVisibility> | undefined): boolean {
  if (item.visibilityAnyOf?.length) {
    return item.visibilityAnyOf.some((key) => fv?.[key] !== false);
  }
  if (item.visibilityKey) {
    return fv?.[item.visibilityKey] !== false;
  }
  return true;
}

const staticMenus: Menu[] = [
  {
    header: '',
    id: 'dashboard',
    items: [{ title: 'DASHBOARD', link: '/dashboard', iconSrc: 'dashboard', isActive: true }],
  },
  {
    header: 'PLANS_AND_USAGES',
    id: 'plans-usages',
    items: [
      { title: 'PLANS', link: '/plans', iconSrc: 'race:receipt-text' },
      { title: 'MY_PLAN_SERVICES', link: '/my-plan', iconSrc: 'race:receipt-text' },
      { title: 'USAGES', link: '/usages', iconSrc: 'race:chart-2' },
      { title: 'TOPUP', link: '/mobile-recharge', iconSrc: 'mobile', visibilityKey: 'mobileTopupVisible' },
      {
        title: 'UTILITY_BILL',
        link: '/utility-bill',
        iconSrc: 'race:receipt-text',
        visibilityKey: 'utilityBillPaymentVisible',
      },
      { title: 'RECHARGE', link: '/recharge', iconSrc: 'wallet', visibilityKey: 'rechargeSectionVisible' },
      { title: 'OTHER_SERVICE', link: '/other-service', iconSrc: 'appstore', visibilityKey: 'otherServicesVisible' },
    ],
  },
  {
    header: 'LOYALTY',
    id: 'loyalty',
    items: [
      {
        title: 'LOYALTY_REWARDS',
        link: '/loyalty/rewards',
        iconSrc: 'race:medal-star',
        visibilityKey: 'loyaltyOptionVisible',
      },
      {
        title: 'LOYALTY_HISTORY',
        link: '/loyalty/history',
        iconSrc: 'race:chart-2',
        visibilityKey: 'loyaltyOptionVisible',
      },
    ],
  },
  {
    header: 'ACCOUNT',
    id: 'account',
    items: [
      { title: 'PROFILE', link: '/profile', iconSrc: 'race:user' },
      { title: 'TRANSACTIONS_HISTORY', link: '/transactions', iconSrc: 'reload', visibilityKey: 'walletVisible' },
    ],
  },
  {
    header: 'SUPPORT',
    id: 'support',
    items: [
      {
        title: 'support.title',
        link: '/support',
        iconSrc: 'race:message',
        nzTheme: 'fill',
        visibilityAnyOf: SUPPORT_MENU_ANY_FEATURES,
      },
      // { title: 'SUBMIT_TICKET', link: '/contact-us', iconSrc: 'question-circle', nzTheme: 'fill' },
    ],
  },
  {
    header: 'COMMUNITY_AND_UPDATES',
    id: 'community-updates',
    items: [
      {
        title: 'ANNOUNCEMENTS',
        link: '/announcements',
        iconSrc: 'race:announcements',
        nzTheme: 'fill',
        visibilityKey: 'announcementsVisible',
      },
      {
        title: 'OFFERS',
        link: '/offers',
        iconSrc: 'gift',
        visibilityKey: 'offersVisible',
      },
      {
        title: 'referral.title',
        link: '/referral',
        iconSrc: 'race:medal-star',
        nzTheme: 'fill',
        // visibilityKey: 'loyaltyOptionVisible',
      },
    ],
  },
];

@Component({
  selector: 'race-sidebar',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    NzMenuItemComponent,
    NzMenuGroupComponent,
    TranslateModule,
    // NzSubMenuComponent,
    NzIconDirective,
    NzInputModule,
    NzTooltipDirective,
    NzMenuDirective,
    // NzSkeletonComponent,
    // TranslatePipe,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  readonly collapsed = input(false);
  currentLanguageKey = 'en';
  currentRoute = '';

  private router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sharedDataService = inject(SharedDataService);

  /** Full menu model (before feature visibility); item `isActive` is updated here. */
  private readonly menuGroups = signal<Menu[]>([]);

  readonly visibleMenus = computed(() => {
    const fv = this.sharedDataService.featureVisibility();
    return this.menuGroups()
      .map((group) => ({
        ...group,
        items: group.items?.filter((item) => isSidebarItemVisible(item, fv)) ?? [],
      }))
      .filter((group) => (group.items?.length ?? 0) > 0);
  });

  searchControl = new FormControl('');

  readonly isMenuLoading = signal<boolean>(false);

  ngOnInit(): void {
    const menus = staticMenus.map((m) => ({
      ...m,
      items: m.items ? m.items.map((it) => ({ ...it })) : [],
    }));
    this.menuGroups.set(menus);

    this.currentRoute = this.router.routerState.snapshot.url;
    this.updateActiveMenu(this.currentRoute);

    // Conditionally show "Other Accounts" under ACCOUNT when parent flag is set in localStorage
    try {
      const isParent = localStorage.getItem('parent') === 'true';
      const accountMenu = menus.find((m) => m.id === 'account');
      if (isParent) {
        if (accountMenu && accountMenu.items && !accountMenu.items.some((i) => i.link === '/other-accounts')) {
          accountMenu.items.push({
            title: 'OTHER_ACCOUNTS',
            link: '/other-accounts',
            iconSrc: 'usergroup-add',
            visibilityKey: 'otherAccountsVisible',
          });
          this.menuGroups.set([...menus]);
          this.cdr.markForCheck();
        }
      } else {
        if (accountMenu && accountMenu.items && accountMenu.items.some((i) => i.link === '/other-accounts')) {
          accountMenu.items = accountMenu.items.filter((i) => i.link !== '/other-accounts');
          this.menuGroups.set([...menus]);
          this.cdr.markForCheck();
        }
      }
    } catch (e) {
      // ignore storage errors
    }

    // Listen to route changes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        this.updateActiveMenu(event.urlAfterRedirects);
      });
  }

  // private manageLanguage(): void {
  //   this.currentLanguageKey = this.translate.currentLang;
  //   if (!this.currentLanguageKey) {
  //     this.currentLanguageKey = this.translate.getDefaultLang();
  //   }
  //   this.translate.onLangChange.subscribe((res) => {
  //     this.currentLanguageKey = res.lang;
  //   });
  // }

  // private checkOpenMenus(currentUrl: string): void {
  //   this.menus.forEach((menu) => {
  //     menu.isMatchedCurrentRoute = false;

  //     if (menu.path && this.isRouteMatch(currentUrl, menu.path)) {
  //       menu.isMatchedCurrentRoute = true;
  //     } else if (menu.subMenus && menu.subMenus.length) {
  //       menu.isMatchedCurrentRoute = menu.subMenus.some((sub) => sub.path && this.isRouteMatch(currentUrl, sub.path));
  //     }
  //   });
  //   this.cdr.markForCheck();
  // }

  // Handle submenu open/close events
  // onSubmenuOpenChange(isOpen: boolean, menu: Menu): void {
  //   if (isOpen) {
  //     // Close all other menus when this one opens
  //     this.filteredMenus.forEach((item) => {
  //       if (item.id !== menu.id) {
  //         item.isExpanded = false;
  //       }
  //     });
  //     // Set this menu as expanded
  //     menu.isExpanded = true;
  //   } else {
  //     // Set this menu as collapsed
  //     menu.isExpanded = false;
  //   }
  //   this.cdr.markForCheck();
  // }

  // Check if menu should be open (for route matching or manual expansion)
  // isMenuOpen(menu: Menu): boolean {
  //   return menu.isExpanded || false;
  // }

  // private isRouteMatch(currentUrl: string, menuPath: string): boolean {
  //   return currentUrl === menuPath || currentUrl.startsWith(menuPath);
  // }

  // getAllMenu(res: Menu[]): void {
  //   // Add bulk upload management menu
  //   const menus = res;
  //   const modifiedMenus = menus.map(setActive);
  //   // Insert bulk upload menu at the beginning of the menus array
  //   this.menus = [...modifiedMenus];
  //   this.filteredMenus = [...this.menus];
  //   console.log('Menus loaded:', this.menus);
  //   this.cdr.markForCheck();
  // }

  searchIt(value: string): void {
    this.cdr.markForCheck();
  }

  private updateActiveMenu(currentUrl: string): void {
    for (const menu of this.menuGroups()) {
      menu.items?.forEach((item) => {
        item.isActive = false;
      });
    }

    for (const menu of this.menuGroups()) {
      menu.items?.forEach((item) => {
        if (item.link && this.isRouteMatch(currentUrl, item.link)) {
          item.isActive = true;
        }
      });
    }

    this.cdr.markForCheck();
  }

  private isRouteMatch(currentUrl: string, menuPath: string): boolean {
    // Exact match or starts with the menu path
    return currentUrl === menuPath || currentUrl.startsWith(menuPath + '/');
  }

  private resetMenuHighlighting(menus: Menu[]): void {
    // menus.forEach((menu) => {
    //   menu.isHighlighted = false;
    //   menu.isExpanded = false;
    //   if (menu.subMenus && menu.subMenus.length > 0) {
    //     menu.subMenus.forEach((subMenu) => {
    //       subMenu.isHighlighted = false;
    //     });
    //   }
    // });
  }

  private processMenusForSearch(menus: Menu[], filterValue: string): void {
    // menus.forEach((menu) => {
    //   // Check if the main menu name matches
    //   const menuMatches = menu.name.toLowerCase().includes(filterValue);
    //   if (menuMatches) {
    //     menu.isHighlighted = true;
    //   }
    //   // Check if any submenu matches
    //   let hasMatchingSubMenu = false;
    //   if (menu.subMenus && menu.subMenus.length) {
    //     menu.subMenus.forEach((subMenu) => {
    //       const subMenuMatches = subMenu.name.toLowerCase().includes(filterValue);
    //       if (subMenuMatches) {
    //         subMenu.isHighlighted = true;
    //         hasMatchingSubMenu = true;
    //       }
    //     });
    //   }
    //   // If any submenu matches, expand the parent menu
    //   if (hasMatchingSubMenu) {
    //     menu.isExpanded = true;
    //   }
    // });
  }
}

// function setActive(menuItem: MenuItem): Menu {
//   return {
//     ...menuItem,
//     active: menuItem.status === 'ACTIVE',
//     subMenus: menuItem.subMenus?.map(setActive) || [],
//   } as Menu;
// }
